// ./src/components/generic/QuickCreateDrawer/QuickCreateDrawer.tsx
//
// The in-place "create it right here" prompt for entities that are just a NAME
// (an RSVP group, a guest group, …). Pickers whose entity needs a real editor
// (category, venue, contact) open their own drawer instead — this is the light
// counterpart, so a one-field entity doesn't get a full drawer.
//
// Pair it with `SelectField`'s `createLabel` / `onCreate`: the picker hands over
// whatever the user typed, this asks for/confirms the name, and the caller's
// `onSubmit` creates the record and returns it so the picker can select it.
//
// ROADMAP #120 (2026-10-08): this was a modal; it is a drawer (house rule: AppDrawer, not a modal). It opens from
// INSIDE another drawer's form, so it stays on local state — a URL stage would take the form under it away.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Stack, Text, TextInput } from "@mantine/core";
import { OpResult } from "@sdflc/api-helpers";

import { getFormErrors, formatErrors } from "@/utils";
import { useFormSnapshot, useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";

import { AppDrawer } from "../AppDrawer";

// The only mapped field — everything else the server complains about is generic.
const KNOWN_FORM_FIELDS = ["name"];

export interface QuickCreateDrawerProps {
  opened: boolean;
  onClose: () => void;
  /** Dialog title, e.g. "New RSVP group". */
  title: string;
  /** Field label (defaults to "Name"). */
  label?: string;
  placeholder?: string;
  description?: string;
  /** Pre-fills the field — pass the text the user already typed in the picker. */
  initialName?: string;
  /** Creates the record. Return the OpResult so errors surface here. */
  onSubmit: (name: string) => Promise<OpResult>;
  /** Called with the created record's id once the create succeeds. */
  onCreated?: (id: string, name: string) => void;
  confirmLabel?: string;
  /** Message shown when the field is left empty. */
  requiredMessage?: string;
}

export const QuickCreateDrawer: React.FC<QuickCreateDrawerProps> = ({
  opened,
  onClose,
  title,
  label = "Name",
  placeholder,
  description,
  initialName = "",
  onSubmit,
  onCreated,
  confirmLabel = "Create",
  requiredMessage = "A name is required",
}) => {
  const [name, setName] = useState(initialName);
  // Local (client-side) required-check, kept separate from the server's answer.
  const [error, setError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<OpResult | null>(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Read at open only: a caller re-rendering with other text must not replace what is being typed.
  const initialNameRef = useRef(initialName);
  initialNameRef.current = initialName;

  // Esc / a click outside / Cancel ask before discarding a typed name (#123).
  const { isDirty, seedFrom, clear } = useFormSnapshot({ name });

  useEffect(() => {
    if (!opened) {
      clear();

      return undefined;
    }

    setName(initialNameRef.current);
    seedFrom({ name: initialNameRef.current });
    setError(null);
    setSubmitError(null);
    setSaving(false);

    // Let the open animation + focus trap settle before grabbing focus.
    const id = setTimeout(() => inputRef.current?.focus(), 120);

    return () => clearTimeout(id);
  }, [opened, seedFrom, clear]);

  const handleSubmit = useCallback(async () => {
    if (saving) {
      return;
    }

    const trimmed = name.trim();

    if (!trimmed) {
      setError(requiredMessage);

      return;
    }

    setSaving(true);
    setError(null);
    setSubmitError(null);

    const result = await onSubmit(trimmed);

    setSaving(false);

    if (!result.didSucceed()) {
      setSubmitError(result);

      return;
    }

    const created = result.getDataFirst() as { id?: string } | undefined;

    if (created?.id) {
      onCreated?.(created.id, trimmed);
    }

    onClose();
  }, [saving, name, onSubmit, onCreated, onClose, requiredMessage]);

  // ⌘S / ⌘Enter only. Esc is AppDrawer's own — it closes only this drawer, not the one it was opened from (the overlay
  // stack), and asks before discarding a typed name.
  useModalShortcuts({ onConfirm: handleSubmit, canConfirm: !saving, enabled: opened });

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(submitError, KNOWN_FORM_FIELDS),
    [submitError],
  );

  const nameError = error ?? fieldErrors.name;

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={title}
      position="right"
      size="sm"
      mobileFitContent
      closable={!saving}
      loading={saving}
      errors={formatErrors(genericErrors)}
      hasFieldErrors={!!nameError}
      actionType="confirm-cancel"
      onConfirm={handleSubmit}
      confirmLabel={confirmLabel}
      confirmIcon="Check"
      confirmShortcut={SHORTCUT_HINTS.save}
      isDirty={isDirty}
    >
      <Stack gap="md">
        {description && (
          <Text size="md" c="dimmed">
            {description}
          </Text>
        )}

        <TextInput
          ref={inputRef}
          label={label}
          placeholder={placeholder}
          value={name}
          onChange={(e) => {
            const value = e.currentTarget.value;
            setName(value);
            setError(null);
            setSubmitError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSubmit();
            }
          }}
          error={nameError}
          size="md"
          data-autofocus
        />
      </Stack>
    </AppDrawer>
  );
};
